import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { rutas } from "../../rutas/rutas";

/* =====================================================
   EcosystemEventWidget.jsx
   Reusable frontend for Ecosystem Events
   Current event: Golden Bone Hunt
   -----------------------------------------------------
   ✅ This file is fixed so it does NOT drop to the bottom.
   ✅ The access card stays fixed in the upper-left area, in the space
      you marked on the main screen.
   ✅ Then you only import it into the main page.

   USAGE IN BIG DOG BONE:
   import EcosystemEventWidget from "../ecosystem/EcosystemEventWidget";
   <EcosystemEventWidget targetGame="bdb" />

   USAGE IN CITY FARM:
   import EcosystemEventWidget from "../ecosystem/EcosystemEventWidget";
   <EcosystemEventWidget targetGame="cityfarm" />

   REQUIRED ROUTE IN rutas.js:
   ecosystemEvents: `${URL_MAIN}/api/ecosystem-events`,
===================================================== */

/* =====================================================
   ACCESS POSITION PANEL
   Here you control where the small button/banner appears.
   If it is a little too high/low, change TOP.
===================================================== */
const ACCESS_POSITION = {
  // false = access stays fixed where you marked it.
  // true  = it renders normally where you place it with JSX.
  INLINE: false,

  // Recommended location for the upper-left BDB space.
  TOP: 42,
  LEFT: 6,

  // Compact access size.
  WIDTH: 106,
  MIN_HEIGHT: 122,

  // Above the game background, below the modal.
  Z_INDEX: 1200,
};

const API_BASE =
  rutas?.ecosystemEvents ||
  (typeof window !== "undefined" && window.location.hostname.includes("cityfarmgame.com")
    ? "https://bigdogbonegame.com/api/ecosystem-events"
    : "/api/ecosystem-events");

const GAME_LABELS = {
  bdb: "Big Dog Bone",
  cityfarm: "City Farm",
  casino: "Casino",
  ecosystem: "Ecosystem",
};

const GAME_EMOJIS = {
  bdb: "🐶",
  cityfarm: "🌾",
  casino: "🎰",
  ecosystem: "🌐",
};

const GAME_ORDER = ["bdb", "casino", "cityfarm", "ecosystem"];

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift() || "";
  return "";
}

function decodeBase64Url(value = "") {
  try {
    const normalized = String(value).replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    return decodeURIComponent(
      atob(padded)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );
  } catch {
    return "";
  }
}

function decodeJwtPayload(token = "") {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const json = decodeBase64Url(parts[1]);
    return json ? JSON.parse(json) : null;
  } catch {
    return null;
  }
}

function normalizeUserName(value) {
  return String(value || "").trim().toLowerCase();
}

function findUserNameFromPayload(payload) {
  if (!payload || typeof payload !== "object") return "";
  const candidates = [
    payload.usuario,
    payload.nombre,
    payload.username,
    payload.user,
    payload.account,
    payload.hiveUser,
    payload.correo,
    payload.email,
    payload.sub,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeUserName(candidate);
    if (normalized) return normalized;
  }
  return "";
}

function readPossibleStoredUserName() {
  if (typeof window === "undefined") return "";

  const directKeys = [
    "nombre",
    "usuario",
    "username",
    "hiveUser",
    "user",
    "userName",
    "account",
  ];

  for (const key of directKeys) {
    const value = normalizeUserName(window.localStorage?.getItem(key) || window.sessionStorage?.getItem(key));
    if (value && value !== "null" && value !== "undefined") return value;
  }

  const jsonKeys = ["informacion", "usuarioData", "userData", "user", "persist:root"];
  for (const key of jsonKeys) {
    const raw = window.localStorage?.getItem(key) || window.sessionStorage?.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      const nested = typeof parsed?.usuario === "string" ? JSON.parse(parsed.usuario) : parsed?.usuario;
      const candidates = [
        parsed?.nombre,
        parsed?.usuario,
        parsed?.username,
        parsed?.correo,
        parsed?.email,
        parsed?.informacion?.nombre,
        parsed?.informacion?.usuario,
        nested?.informacion?.nombre,
        nested?.nombre,
        nested?.usuario,
      ];
      for (const candidate of candidates) {
        const value = normalizeUserName(candidate);
        if (value && value !== "null" && value !== "undefined") return value;
      }
    } catch {
      // Ignore local data that is not JSON.
    }
  }

  return "";
}

function getAuthContext(explicitUserName = "") {
  const token = readCookie("token");
  const payload = decodeJwtPayload(token);
  const nombre =
    normalizeUserName(explicitUserName) ||
    findUserNameFromPayload(payload) ||
    readPossibleStoredUserName();

  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers.token = token;
    headers["x-token"] = token;
  }

  return { token, nombre, headers };
}

function buildApiUrl(path, nombre = "") {
  const cleanPath = String(path || "").startsWith("/") ? path : `/${path}`;
  const base = `${API_BASE}${cleanPath}`;
  const n = normalizeUserName(nombre);
  if (!n) return base;
  const joiner = base.includes("?") ? "&" : "?";
  return `${base}${joiner}nombre=${encodeURIComponent(n)}`;
}

function getErrorMessage(err, fallback = "Could not load event") {
  const data = err?.response?.data;
  const status = err?.response?.status;
  const serverMessage = data?.message || data?.detail || data?.error;

  if (status && serverMessage) return `HTTP ${status}: ${serverMessage}`;
  if (serverMessage) return String(serverMessage);
  if (err?.message === "Network Error") {
    return "Network Error: check CORS, backend URL, or whether /api/ecosystem-events is mounted.";
  }
  return err?.message || fallback;
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, value));
}

function formatNumber(value, decimals = 3) {
  const number = safeNumber(value);
  return number.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function groupMissions(missions = []) {
  const grouped = missions.reduce((acc, mission) => {
    const game = mission?.game || "ecosystem";
    if (!acc[game]) acc[game] = [];
    acc[game].push(mission);
    return acc;
  }, {});

  return GAME_ORDER.filter((game) => grouped[game]?.length).map((game) => ({
    game,
    missions: grouped[game],
  }));
}

function getProgressPercent(current, goal) {
  const g = Math.max(1, safeNumber(goal, 1));
  const c = safeNumber(current, 0);
  return clamp((c / g) * 100, 0, 100);
}

function StatCard({ label, value, helper, glow = false }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.35,
        borderRadius: 3,
        minHeight: 82,
        border: "1px solid rgba(255,255,255,0.13)",
        background: glow
          ? "linear-gradient(145deg, rgba(255,213,83,0.22), rgba(255,255,255,0.07))"
          : "rgba(255,255,255,0.07)",
        boxShadow: glow ? "0 0 30px rgba(255,204,77,0.12)" : "none",
      }}
    >
      <Typography sx={{ color: "rgba(255,255,255,0.64)", fontSize: "0.76rem", fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography sx={{ color: "#fff", fontSize: { xs: "1.15rem", sm: "1.35rem" }, fontWeight: 950 }}>
        {value}
      </Typography>
      {helper && (
        <Typography sx={{ color: "rgba(255,255,255,0.50)", fontSize: "0.72rem" }}>
          {helper}
        </Typography>
      )}
    </Paper>
  );
}

function MissionCard({ mission }) {
  const completed = Boolean(mission?.completed || mission?.alreadyAwarded);
  const justAwarded = Boolean(mission?.justAwarded);
  const percent = getProgressPercent(mission?.current, mission?.goal);

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: 3,
        p: 1.6,
        minHeight: 128,
        border: completed
          ? "1px solid rgba(79, 255, 176, 0.45)"
          : "1px solid rgba(255,255,255,0.12)",
        background: completed
          ? "linear-gradient(145deg, rgba(30, 130, 80, 0.30), rgba(9, 18, 35, 0.95))"
          : "linear-gradient(145deg, rgba(255,255,255,0.08), rgba(9, 18, 35, 0.95))",
        boxShadow: completed
          ? "0 0 28px rgba(51,255,158,0.12) inset"
          : "0 16px 35px rgba(0,0,0,0.22)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -35,
          right: -35,
          width: 95,
          height: 95,
          borderRadius: "50%",
          background: completed
            ? "radial-gradient(circle, rgba(88,255,181,0.30), transparent 65%)"
            : "radial-gradient(circle, rgba(255,202,80,0.18), transparent 65%)",
        }}
      />

      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 900,
              color: "#fff",
              fontSize: { xs: "0.88rem", sm: "0.94rem" },
              lineHeight: 1.25,
            }}
          >
            {completed ? "✅" : "⏳"} {mission?.label || "Mission"}
          </Typography>

          <Typography sx={{ mt: 0.7, color: "rgba(255,255,255,0.68)", fontSize: "0.78rem" }}>
            Progress: {safeNumber(mission?.current)}/{safeNumber(mission?.goal)}
            {safeNumber(mission?.rawCurrent) > safeNumber(mission?.goal)
              ? ` · total detectado: ${safeNumber(mission?.rawCurrent)}`
              : ""}
          </Typography>
        </Box>

        <Chip
          size="small"
          label={`+${safeNumber(mission?.fragments)} 🦴`}
          sx={{
            flexShrink: 0,
            color: "#241500",
            fontWeight: 900,
            background: "linear-gradient(135deg, #ffd66b, #ff9f1c)",
            boxShadow: "0 0 18px rgba(255,193,7,0.25)",
          }}
        />
      </Stack>

      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          mt: 1.4,
          height: 8,
          borderRadius: 10,
          backgroundColor: "rgba(255,255,255,0.10)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 10,
            background: completed
              ? "linear-gradient(90deg, #40ff9f, #c6ff6d)"
              : "linear-gradient(90deg, #ffcc4d, #ff7d1a)",
          },
        }}
      />

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1.1 }}>
        <Typography sx={{ color: completed ? "#89ffbd" : "rgba(255,255,255,0.56)", fontSize: "0.74rem", fontWeight: 800 }}>
          {completed ? "Completada" : "Pendiente"}
        </Typography>
        {justAwarded && (
          <Chip
            size="small"
            label="Nuevo"
            sx={{
              height: 20,
              fontSize: "0.68rem",
              color: "#001f12",
              fontWeight: 900,
              background: "#64ffac",
            }}
          />
        )}
      </Stack>
    </Paper>
  );
}

export default function EcosystemEventWidget({
  targetGame = "bdb",
  userName = "",
  nombre = "",
  defaultOpen = false,
  compact = true,
  autoLoad = true,
  inline = ACCESS_POSITION.INLINE,
  top = ACCESS_POSITION.TOP,
  left = ACCESS_POSITION.LEFT,
  width = ACCESS_POSITION.WIDTH,
  zIndex = ACCESS_POSITION.Z_INDEX,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [banner, setBanner] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(targetGame === "cityfarm" ? "cityfarm" : "bdb");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const authContext = useMemo(() => getAuthContext(nombre || userName), [nombre, userName]);

  const config = data?.config || {};
  const progress = data?.progress || {};
  const missions = data?.missions || [];
  const missionGroups = useMemo(() => groupMissions(missions), [missions]);

  const maxDailyFragments = safeNumber(config?.limits?.maxFragmentsPerUserDay, 10);
  const todayFragments = safeNumber(progress?.todayFragments ?? banner?.todayFragments, 0);
  const dailyPercent = getProgressPercent(todayFragments, maxDailyFragments);

  const payActive = Boolean(config?.payViphueso ?? banner?.payViphueso ?? false);
  const eventActive = Boolean(config?.enabled ?? banner?.active ?? true);
  const fragmentsAvailable = safeNumber(progress?.fragmentsAvailable ?? banner?.fragmentsAvailable, 0);
  const claimableViphueso = safeNumber(progress?.claimableViphueso ?? banner?.claimableViphueso, 0);

  const completedCount = missions.filter((m) => m?.completed || m?.alreadyAwarded).length;
  const totalMissions = missions.length || 0;

  const requestConfig = useMemo(
    () => ({
      headers: authContext.headers,
      withCredentials: true,
    }),
    [authContext.headers]
  );

  const loadBanner = useCallback(async () => {
    try {
      const res = await axios.get(buildApiUrl("/banner", authContext.nombre), requestConfig);
      if (res.data?.ok) setBanner(res.data);
    } catch {
      // The banner must not break the page if the user is not authenticated yet.
      setBanner((prev) =>
        prev || {
          ok: false,
          active: true,
          eventName: "Golden Bone Hunt",
          text: "Missions diarias",
          todayFragments: 0,
          fragmentsAvailable: 0,
          claimableViphueso: 0,
          payViphueso: false,
        }
      );
    }
  }, [requestConfig, authContext.nombre]);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(buildApiUrl("/status", authContext.nombre), requestConfig);
      if (!res.data?.ok) throw new Error(res.data?.message || res.data?.error || "Could not load event");
      setData(res.data);
    } catch (err) {
      setError(getErrorMessage(err, "Could not load event"));
    } finally {
      setLoading(false);
    }
  }, [requestConfig, authContext.nombre]);

  const syncEvent = useCallback(async () => {
    setSyncing(true);
    setError("");
    setMessage("");
    try {
      const res = await axios.post(buildApiUrl("/sync", authContext.nombre), { nombre: authContext.nombre }, requestConfig);
      if (!res.data?.ok) throw new Error(res.data?.message || res.data?.error || "Could not sync event");
      setData((prev) => ({ ...(prev || {}), ...res.data, config: prev?.config || res.data?.config }));
      const awarded = Array.isArray(res.data?.awarded) ? res.data.awarded : [];
      setMessage(
        awarded.length
          ? `Added ${awarded.reduce((sum, item) => sum + safeNumber(item.fragments), 0)} new fragments.`
          : "Sync complete. No new missions to claim."
      );
      await loadBanner();
    } catch (err) {
      setError(getErrorMessage(err, "Error sincronizando"));
    } finally {
      setSyncing(false);
    }
  }, [requestConfig, loadBanner, authContext.nombre]);

  const claimReward = useCallback(async () => {
    setClaiming(true);
    setError("");
    setMessage("");
    try {
      const res = await axios.post(buildApiUrl("/claim", authContext.nombre), { targetGame: selectedTarget, nombre: authContext.nombre }, requestConfig);
      if (!res.data?.ok) throw { response: { data: res.data } };
      setMessage(`VIPHUESO reclamado: ${formatNumber(res.data?.amount)} ${res.data?.rewardToken || "VIPHUESO"}.`);
      await loadStatus();
      await loadBanner();
    } catch (err) {
      setError(getErrorMessage(err, "Error reclamando recompensa"));
      await loadStatus();
    } finally {
      setClaiming(false);
    }
  }, [selectedTarget, requestConfig, loadStatus, loadBanner, authContext.nombre]);

  useEffect(() => {
    if (autoLoad) loadBanner();
  }, [autoLoad, loadBanner]);

  useEffect(() => {
    if (open) loadStatus();
  }, [open, loadStatus]);

  const bannerTitle = banner?.eventName || config?.eventName || "Golden Bone Hunt";
  const bannerText = banner?.text || (payActive ? "Claim VIPHUESO" : "Test mode: collect fragments");

  const accessWrapperSx = inline
    ? { width: "100%" }
    : {
        position: "fixed",
        top,
        left,
        width,
        zIndex,
      };

  return (
    <>
      <Box
        sx={{
          ...accessWrapperSx,
          "@keyframes ecosystemPulse": {
            "0%": { boxShadow: "0 0 0 rgba(255, 198, 65, 0.0)", transform: "translateY(0)" },
            "50%": { boxShadow: "0 0 26px rgba(255, 198, 65, 0.45)", transform: "translateY(-1px)" },
            "100%": { boxShadow: "0 0 0 rgba(255, 198, 65, 0.0)", transform: "translateY(0)" },
          },
          "@keyframes ecosystemShine": {
            "0%": { transform: "translateX(-120%) rotate(15deg)" },
            "100%": { transform: "translateX(260%) rotate(15deg)" },
          },
          "@keyframes ecosystemFloat": {
            "0%, 100%": { transform: "translateY(0)" },
            "50%": { transform: "translateY(-3px)" },
          },
        }}
      >
        {inline ? (
          <Paper
            onClick={() => setOpen(true)}
            elevation={0}
            sx={{
              cursor: "pointer",
              position: "relative",
              overflow: "hidden",
              borderRadius: 4,
              p: { xs: 1.6, sm: 2 },
              border: eventActive ? "1px solid rgba(255,220,107,0.42)" : "1px solid rgba(255,255,255,0.15)",
              background: eventActive
                ? "linear-gradient(135deg, #130b24 0%, #28114a 34%, #5f3100 72%, #a76600 100%)"
                : "linear-gradient(135deg, #1a1a1f, #30303a)",
              color: "#fff",
              animation: eventActive ? "ecosystemPulse 2.8s ease-in-out infinite" : "none",
            }}
          >
            <Stack direction="row" spacing={1.4} alignItems="center">
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  fontSize: "2.2rem",
                  background: "linear-gradient(135deg, #fff0a6, #ff9f1c)",
                  boxShadow: "0 0 24px rgba(255,184,31,0.35)",
                }}
              >
                🦴
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontWeight: 950, fontSize: { xs: "1rem", sm: "1.18rem" }, lineHeight: 1.1 }}>
                  {bannerTitle}
                </Typography>
                <Typography sx={{ mt: 0.45, color: "rgba(255,255,255,0.80)", fontSize: "0.84rem" }}>
                  {bannerText}
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={{
                  color: "#241400",
                  fontWeight: 950,
                  borderRadius: 99,
                  px: 2.2,
                  background: "linear-gradient(135deg, #fff176, #ffae20)",
                }}
              >
                View missions
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Tooltip title="Golden Bone Hunt" placement="right" arrow>
            <Paper
              onClick={() => setOpen(true)}
              elevation={0}
              sx={{
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                width: "100%",
                minHeight: ACCESS_POSITION.MIN_HEIGHT,
                borderRadius: "12px",
                px: 0.7,
                py: 0.9,
                color: "#fff",
                textAlign: "center",
                border: "2px solid rgba(255, 42, 42, 0.95)",
                background:
                  "linear-gradient(180deg, rgba(9, 13, 31, 0.96) 0%, rgba(25, 12, 42, 0.94) 54%, rgba(71, 37, 0, 0.92) 100%)",
                boxShadow: "0 0 18px rgba(255, 42, 42, 0.25), 0 14px 30px rgba(0,0,0,0.38)",
                animation: eventActive ? "ecosystemPulse 2.9s ease-in-out infinite" : "none",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 0 28px rgba(255, 213, 83, 0.45), 0 18px 40px rgba(0,0,0,0.5)",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "radial-gradient(circle at 50% 0%, rgba(255,220,90,0.22), transparent 38%), radial-gradient(circle at 50% 85%, rgba(121,52,255,0.22), transparent 42%)",
                  pointerEvents: "none",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: -55,
                  left: -60,
                  width: 55,
                  height: 190,
                  background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.27), transparent)",
                  animation: "ecosystemShine 3.4s linear infinite",
                  pointerEvents: "none",
                }}
              />

              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box
                  sx={{
                    mx: "auto",
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    fontSize: "1.72rem",
                    background: "linear-gradient(135deg, #fff2aa, #ff9f1c)",
                    boxShadow: "0 0 22px rgba(255,184,31,0.48)",
                    animation: "ecosystemFloat 3s ease-in-out infinite",
                  }}
                >
                  🦴
                </Box>

                <Typography
                  sx={{
                    mt: 0.8,
                    fontSize: "0.66rem",
                    lineHeight: 1.05,
                    fontWeight: 950,
                    textTransform: "uppercase",
                    textShadow: "0 2px 10px rgba(0,0,0,0.65)",
                  }}
                >
                  Bone
                  <br />
                  Hunt
                </Typography>

                <Chip
                  size="small"
                  label={payActive ? "VIP ON" : "TEST"}
                  sx={{
                    mt: 0.65,
                    height: 18,
                    fontSize: "0.56rem",
                    fontWeight: 950,
                    color: payActive ? "#001f12" : "#2b1600",
                    background: payActive ? "#63ffac" : "#ffd66b",
                    "& .MuiChip-label": { px: 0.7 },
                  }}
                />

                <Typography sx={{ mt: 0.55, color: "rgba(255,255,255,0.72)", fontSize: "0.58rem", lineHeight: 1.1 }}>
                  {formatNumber(banner?.todayFragments || 0, 0)} today · {formatNumber(banner?.fragmentsAvailable || 0, 0)} available
                </Typography>

                <Button
                  size="small"
                  sx={{
                    mt: 0.75,
                    minWidth: 0,
                    width: "100%",
                    height: 22,
                    borderRadius: 99,
                    color: "#261400",
                    fontWeight: 950,
                    fontSize: "0.58rem",
                    background: "linear-gradient(135deg, #fff176, #ffae20)",
                    boxShadow: "0 0 15px rgba(255,174,32,0.38)",
                    "&:hover": { background: "linear-gradient(135deg, #fff7a6, #ffc14d)" },
                  }}
                >
                  VIEW
                </Button>
              </Box>
            </Paper>
          </Tooltip>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 4,
            overflow: "hidden",
            background: "linear-gradient(145deg, #070b18, #101b34 44%, #251039 100%)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            overflow: "hidden",
            p: { xs: 2, sm: 2.5 },
            background:
              "radial-gradient(circle at 15% 20%, rgba(255,221,99,0.22), transparent 25%), linear-gradient(135deg, rgba(255,255,255,0.07), transparent)",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1.4}>
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  fontSize: "1.9rem",
                  background: "linear-gradient(135deg, #fff0a6, #ff9f1c)",
                  boxShadow: "0 0 24px rgba(255,184,31,0.35)",
                }}
              >
                🦴
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 950, fontSize: { xs: "1.05rem", sm: "1.28rem" }, lineHeight: 1.1 }}>
                  {config?.eventName || bannerTitle}
                </Typography>
                <Typography sx={{ mt: 0.35, color: "rgba(255,255,255,0.70)", fontSize: "0.8rem" }}>
                  Daily missions · Automatic reset at 00:00 UTC · VIPHUESO-only reward
                </Typography>
              </Box>
            </Stack>

            <IconButton onClick={() => setOpen(false)} sx={{ color: "rgba(255,255,255,0.80)" }}>
              ✕
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 1.6, sm: 2.4 }, background: "rgba(0,0,0,0.08)" }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2, color: "rgba(255,255,255,0.72)" }}>
                Loading event...
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.8}>
              {error && (
                <Alert
                  severity="warning"
                  onClose={() => setError("")}
                  sx={{
                    borderRadius: 3,
                    color: "#ffdf9a",
                    background: "rgba(50, 25, 0, 0.85)",
                    border: "1px solid rgba(255,193,7,0.2)",
                    "& .MuiAlert-icon": { color: "#ffbf35" },
                  }}
                >
                  {error}
                </Alert>
              )}

              {message && (
                <Alert
                  severity="success"
                  onClose={() => setMessage("")}
                  sx={{
                    borderRadius: 3,
                    color: "#d6ffe9",
                    background: "rgba(0, 50, 25, 0.65)",
                    border: "1px solid rgba(80,255,170,0.2)",
                    "& .MuiAlert-icon": { color: "#63ffac" },
                  }}
                >
                  {message}
                </Alert>
              )}

              {!payActive && (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 3,
                    color: "#bceeff",
                    background: "rgba(0, 27, 47, 0.68)",
                    border: "1px solid rgba(91, 204, 255, 0.18)",
                    "& .MuiAlert-icon": { color: "#55d6ff" },
                  }}
                >
                  The event is in <b>test mode</b>: missions add fragments, but VIPHUESO claiming is disabled from the backend.
                </Alert>
              )}

              <Grid container spacing={1.2}>
                <Grid item xs={6} md={3}>
                  <StatCard
                    label="Fragments today"
                    value={`${formatNumber(todayFragments, 0)}/${formatNumber(maxDailyFragments, 0)}`}
                    helper="Daily cap"
                    glow={todayFragments > 0}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    label="Available"
                    value={`${formatNumber(fragmentsAvailable, 0)} 🦴`}
                    helper="To claim"
                    glow={fragmentsAvailable > 0}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    label="Estimated VIPHUESO"
                    value={formatNumber(claimableViphueso)}
                    helper={`${formatNumber(config?.viphuesoPerFragment || 0.025)} per fragment`}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatCard
                    label="Missions"
                    value={`${completedCount}/${totalMissions}`}
                    helper="Completed today"
                  />
                </Grid>
              </Grid>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: 1.4,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                }}
              >
                <Stack direction={{ xs: "column", md: "row" }} spacing={1.4} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.7 }}>
                      <Typography sx={{ fontWeight: 900 }}>Daily progress</Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.70)", fontSize: "0.78rem", fontWeight: 800 }}>
                        {Math.round(dailyPercent)}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={dailyPercent}
                      sx={{
                        height: 10,
                        borderRadius: 10,
                        backgroundColor: "rgba(255,255,255,0.10)",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 10,
                          background: "linear-gradient(90deg, #ffd66b, #ff9f1c, #63ffac)",
                        },
                      }}
                    />
                    <Typography sx={{ mt: 0.7, color: "rgba(255,255,255,0.55)", fontSize: "0.74rem" }}>
                      UTC day: {data?.dayKey || banner?.dayKey || "—"} · Season: {data?.seasonId || banner?.seasonId || "—"}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                    <Button
                      variant="contained"
                      disabled={syncing || !eventActive}
                      onClick={syncEvent}
                      sx={{
                        borderRadius: 99,
                        color: "#241500",
                        fontWeight: 950,
                        background: "linear-gradient(135deg, #fff176, #ffae20)",
                        "&:hover": { background: "linear-gradient(135deg, #fff7a6, #ffc14d)" },
                      }}
                    >
                      {syncing ? "Updating..." : "Update missions"}
                    </Button>

                    <Tooltip title={!payActive ? "Claim is disabled in test mode" : "Claim VIPHUESO"} arrow>
                      <span>
                        <Button
                          variant="outlined"
                          disabled={claiming || !eventActive || !payActive || fragmentsAvailable <= 0}
                          onClick={claimReward}
                          sx={{
                            borderRadius: 99,
                            color: "#fff",
                            fontWeight: 950,
                            borderColor: "rgba(255,255,255,0.26)",
                            "&:hover": { borderColor: "#ffd66b", background: "rgba(255,214,107,0.08)" },
                          }}
                        >
                          {claiming ? "Claiming..." : "Claim VIPHUESO"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: 1.35,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.055)",
                }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>Reward destination</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: "0.78rem" }}>
                      BDB suma en <b>fusion</b>. City Farm suma en <b>viphueso</b>.
                    </Typography>
                  </Box>

                  <Select
                    size="small"
                    value={selectedTarget}
                    onChange={(e) => setSelectedTarget(e.target.value)}
                    sx={{
                      minWidth: 180,
                      color: "#fff",
                      borderRadius: 99,
                      background: "rgba(255,255,255,0.08)",
                      ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.18)" },
                      ".MuiSvgIcon-root": { color: "#fff" },
                    }}
                  >
                    <MenuItem value="bdb">🐶 Big Dog Bone</MenuItem>
                    <MenuItem value="cityfarm">🌾 City Farm</MenuItem>
                  </Select>
                </Stack>
              </Paper>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.10)" }} />

              {missionGroups.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    p: 3,
                    textAlign: "center",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Typography sx={{ color: "rgba(255,255,255,0.68)", fontWeight: 800 }}>
                    No missions loaded yet. Press “Update missions”.
                  </Typography>
                </Paper>
              ) : (
                <Stack spacing={2.2}>
                  {missionGroups.map((group) => (
                    <Box key={group.game}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography sx={{ fontWeight: 950, fontSize: "1rem" }}>
                          {GAME_EMOJIS[group.game] || "🌐"} {GAME_LABELS[group.game] || group.game}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${group.missions.filter((m) => m.completed || m.alreadyAwarded).length}/${group.missions.length}`}
                          sx={{ color: "#fff", background: "rgba(255,255,255,0.12)", fontWeight: 900 }}
                        />
                      </Stack>

                      <Grid container spacing={1.2}>
                        {group.missions.map((mission) => (
                          <Grid item xs={12} sm={6} md={4} key={mission.key}>
                            <MissionCard mission={mission} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              )}

              <Paper
                elevation={0}
                sx={{
                  mt: 0.5,
                  borderRadius: 3,
                  p: 1.4,
                  background: "rgba(0,0,0,0.22)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: "0.76rem" }}>
                  Last sync: {formatDateTime(progress?.lastSyncAt)} · Last claim: {formatDateTime(progress?.lastClaimAt)}
                </Typography>
              </Paper>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
